import React, { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { apiClient } from '../services/api';
import { BoardEntry, ChatMessage, EventBoard } from '../types';
import { FigmaIcon } from '../assets/icons';
import { BoardVote } from './BoardVote';
import { errorText } from './errors';
import { useOrganizer } from './i18n';

type Tab = 'faq' | 'suggestions';

interface Props {
  /** Read as an account holder. */
  eventId?: string;
  /** Read as a guest, with the token they hold. */
  guestToken?: string;
  /** Limit the host's panel to one agenda; null means none is running. */
  agendaSessionId?: string | null;
  /** Poll, since this is watched while a event runs. */
  refreshMs?: number;
  /**
   * Whether this pair of hands sorts what comes in. The host's, and their
   * co-hosts'; everybody else only reads the board and votes on it.
   */
  canSort?: boolean;
  /** The live room handles moderation in its separate Requests panel. */
  showRequests?: boolean;
  /** The host reads the tally; only attendees cast a vote. */
  canVote?: boolean;
  /**
   * What the room's own socket has heard arrive since the last read.
   *
   * The queue is fetched on a timer, which is fine for a list that
   * changes every few minutes and wrong for one somebody is watching
   * while a talk runs: a question asked at the front should be sortable
   * now, not in twenty seconds. The socket already tells the room; this
   * is the room passing it on.
   */
  waiting?: ChatMessage[];
  /**
   * Something new has appeared on the board.
   *
   * The board grows when the host puts a question up, and nothing on the
   * socket says so - this panel finds out by reading it. Whoever is
   * showing the panel wants to know too, so that a control nobody has
   * open can say how much has arrived behind it.
   */
  onNews?: (many: number) => void;
  /**
   * Put something to the host, from the tab it would appear on.
   *
   * Nothing written here is on the board yet. It waits in the host's
   * requests until they put it up as a question or a suggestion, or
   * decline it - and anything still waiting when the session ends is
   * thrown away rather than kept.
   */
  onAsk?: (body: string, topic: 'faq' | 'suggestion') => Promise<void> | void;
}

/**
 * The board, as the room sees it.
 *
 * A question, the room's sense of how much it wants answering, and a vote
 * either way. Who it was originally written to, and what the host did
 * with the other messages, are not the reader's business and are not
 * here.
 *
 * The asker's name is the one exception, and only for whoever sorts the
 * board: the room reads a question on its merits, but the person
 * deciding what goes up in front of a hall has to know whose words they
 * are putting there.
 *
 * The host may show pending requests with the matching board tab, while
 * attendees can read, vote, and send their own messages here.
 */
export const RoomQuestions: React.FC<Props> = ({
  eventId, guestToken, agendaSessionId, refreshMs, canSort, canVote = true,
  showRequests = true, waiting: alsoWaiting, onNews, onAsk,
}) => {
  const { t, num } = useOrganizer();
  const [board, setBoard] = useState<EventBoard | null>(null);
  const [fetched, setFetched] = useState<ChatMessage[]>([]);
  /** What has been dealt with here, so a poll cannot bring it back. */
  /**
   * Ones this screen has already dealt with.
   *
   * Kept because the queue is polled: a message decided a moment ago
   * comes back in the next answer until the server catches up, and the
   * count against the tab should not flicker back up in the meantime.
   */
  const [settled, setSettled] = useState<string[]>([]);
  const [tab, setTab] = useState<Tab>('faq');
  const [busy, setBusy] = useState<string | null>(null);
  const [writing, setWriting] = useState('');
  const [sending, setSending] = useState(false);

  /** How much was on the board when it was last read. */
  const wasOnBoard = useRef<number | null>(null);

  const load = useCallback(async () => {
    try {
      let read: EventBoard | null = null;
      if (guestToken) read = await apiClient.getGuestBoard(guestToken);
      else if (eventId) read = await apiClient.getEventBoard(eventId);
      setBoard(read);

      const now = read ? read.faq.length + read.suggestions.length : 0;
      const before = wasOnBoard.current;
      wasOnBoard.current = now;
      // The first read is what is already there, not news.
      if (before !== null && now > before) onNews?.(now - before);
    } catch {
      setBoard({ faq: [], suggestions: [] });
    }

    if (!canSort || !showRequests || !eventId) { setFetched([]); return; }
    try {
      /*
       * Two kinds of thing are waiting to be given a place.
       *
       * What is held for review, and what has already reached the person
       * it was written to but has never been filed - a message let
       * through with no topic is on nobody's board and in nobody's queue.
       * That is where a question put to the host used to disappear to,
       * and it is still where anything approved from the chat panel
       * lands. Both are unanswered questions, so both are here.
       */
      const [held, seen] = await Promise.all([
        apiClient.getPendingMessages(eventId),
        apiClient.getReviewedMessages(eventId),
      ]);
      const unfiled = [...seen.from_users, ...seen.from_guests].filter(
        (m) => !m.topic || m.topic === 'none'
      );
      setFetched([...held, ...unfiled]);
    } catch {
      // The queue is the host's own view; a dropped refresh is not worth
      // interrupting a event for.
    }
  }, [eventId, guestToken, canSort, showRequests, onNews]);

  useEffect(() => {
    load();
    if (!refreshMs) return;
    const id = setInterval(load, refreshMs);
    return () => clearInterval(id);
  }, [load, refreshMs]);

  /** One vote each. The room decides what most wants answering. */
  const vote = async (entry: BoardEntry, value: 1 | -1) => {
    if (!canVote) return;
    try {
      setBusy(entry.id);
      const updated = guestToken
        ? await apiClient.guestVoteOnBoard(guestToken, entry.id, value)
        : eventId
        ? await apiClient.voteOnBoard(eventId, entry.id, value)
        : null;
      if (updated) setBoard(updated);
    } catch (e: any) {
      toast.error(errorText(e, t({ ne: 'भोट दिन सकिएन', en: 'Could not vote' })));
    } finally { setBusy(null); }
  };

  /*
   * The queue: what the last read found, plus whatever has arrived over
   * the socket since, minus anything already dealt with here.
   */
  const waiting: ChatMessage[] = [];
  const seen = new Set<string>();
  [...fetched, ...(alsoWaiting ?? [])].forEach((m) => {
    if (settled.includes(m.id) || seen.has(m.id)) return;
    seen.add(m.id);
    waiting.push(m);
  });

  const agendaRows = (items: BoardEntry[]) =>
    agendaSessionId === undefined
      ? items
      : items.filter((entry) => entry.session_id === agendaSessionId);
  const questions = agendaRows(board?.faq ?? []);
  const suggestions = agendaRows(board?.suggestions ?? []);
  const rows: BoardEntry[] =
    tab === 'faq' ? questions : tab === 'suggestions' ? suggestions : [];
  const agendaWaiting = agendaSessionId === undefined
    ? waiting
    : waiting.filter((message) => message.session === agendaSessionId);
  const tabRequests = canSort && showRequests
    ? agendaWaiting.filter((message) =>
        message.moderation_status === 'pending'
        && (tab === 'faq'
          ? message.topic !== 'suggestion'
          : message.topic === 'suggestion')
      )
    : [];

  const tabs: { id: Tab; label: string; count: number }[] = [
    {
      id: 'faq',
      label: t({ ne: 'प्रश्न', en: 'Questions' }),
      count: questions.length + (canSort && showRequests
        ? agendaWaiting.filter((message) =>
            message.moderation_status === 'pending' && message.topic !== 'suggestion'
          ).length
        : 0),
    },
    {
      id: 'suggestions',
      label: t({ ne: 'सुझाव', en: 'Suggestions' }),
      count: suggestions.length + (canSort && showRequests
        ? agendaWaiting.filter((message) =>
            message.moderation_status === 'pending' && message.topic === 'suggestion'
          ).length
        : 0),
    },
  ];

  const decideRequest = async (message: ChatMessage, decision: 'approve' | 'decline') => {
    if (!eventId) return;
    setBusy(message.id);
    try {
      await apiClient.moderateMessage(
        eventId,
        message.id,
        decision,
        decision === 'approve' ? (message.topic === 'suggestion' ? 'suggestion' : 'faq') : undefined
      );
      setSettled((previous) => [...previous, message.id]);
      toast.success(decision === 'approve'
        ? t({ ne: 'अनुरोध स्वीकार गरियो', en: 'Request accepted' })
        : t({ ne: 'अनुरोध अस्वीकार गरियो', en: 'Request rejected' }));
      if (decision === 'approve') {
        try {
          setBoard(await apiClient.getEventBoard(eventId));
        } catch (e: any) {
          toast.error(errorText(
            e,
            t({ ne: 'बोर्ड ताजा गर्न सकिएन', en: 'Could not refresh the board' })
          ));
        }
      }
    } catch (e: any) {
      toast.error(errorText(
        e,
        t({ ne: 'अनुरोध पूरा गर्न सकिएन', en: 'Could not process request' })
      ));
    } finally {
      setBusy(null);
    }
  };

  /** One question, with the room's vote on it. */
  const entryRow = (entry: BoardEntry) => (
    <li key={entry.id} className="flex flex-col gap-[7px] px-2 py-1">
      <div className="flex gap-3 items-start">
        <FigmaIcon name="asked" size={24} />
        <p className="flex-1 min-w-0 text-[14px] leading-5 text-[#24262b]">
          {entry.body}
        </p>
      </div>

      {/* Who asked, under the words and to the right of them.

          Only for whoever sorts the board. The room reads a question on
          its merits and the asker's name is nobody else's business
          there, but the person deciding what goes up is answering to a
          hall and has to know who they are putting in front of it. */}
      {canSort && entry.asked_by && (
        <p className="text-[12px] leading-4 text-[#656565] text-right">
          {entry.asked_by}
        </p>
      )}

      {/* Indented to the question, not centred under it: the arrows
          belong to the words above them, and a row of its own in the
          middle of the card reads as something else entirely.

          Only on the questions: a vote sorts a queue, and a suggestion
          is not queued. */}
      {tab === 'faq' && canVote && (
        <div className="ps-9">
          <BoardVote
            entry={entry}
            busy={busy === entry.id}
            onVote={(value) => vote(entry, value)}
          />
        </div>
      )}

      {entry.answer && (
        <p className="ms-9 text-[13px] leading-5 text-[#1B7F58] bg-[#1B7F58]/[.07]
          rounded-[8px] px-2.5 py-1.5">
          {entry.answer}
        </p>
      )}
    </li>
  );

  const nothingYet =
    tab === 'faq'
      ? t({
          ne: 'अझै कुनै प्रश्न राखिएको छैन।',
          en: 'No questions up yet.',
        })
      : t({
          ne: 'अझै कुनै सुझाव राखिएको छैन।',
          en: 'No suggestions up yet.',
        });

  /** Say it, and wait for the host to put it up. */
  const ask = async () => {
    const said = writing.trim();
    if (!said || !onAsk) return;
    try {
      setSending(true);
      await onAsk(said, tab === 'faq' ? 'faq' : 'suggestion');
      setWriting('');
      toast.success(t({
        ne: 'पठाइयो। आयोजकले राखेपछि बोर्डमा देखिन्छ।',
        en: 'Sent. It appears on the board once the host puts it up.',
      }));
    } catch (e: any) {
      toast.error(errorText(e, t({ ne: 'पठाउन सकिएन', en: 'Could not send it' })));
    } finally {
      setSending(false);
    }
  };

  return (
    <div className="flex flex-col">
      <div className="flex border-b border-[#e3e8ef]" role="tablist">
        {tabs.map((one) => (
          <button
            key={one.id}
            role="tab"
            aria-selected={tab === one.id}
            onClick={() => setTab(one.id)}
            className={`flex-1 py-2 text-[13px] font-medium transition ${
              tab === one.id
                ? 'text-black border-b-2 border-black'
                : 'text-[#49454f] hover:text-black'
            }`}
          >
            {one.label} ({num(one.count)})
          </button>
        ))}
      </div>

      <div className="max-h-[441px] overflow-y-auto">
        <ul className="max-h-[210px] overflow-y-auto overscroll-contain
          flex flex-col gap-[13px] px-2 py-3">
          {rows.length === 0
            ? <li className="text-[13px] text-[#656565] px-2">{nothingYet}</li>
            : rows.map(entryRow)}
        </ul>
        {tabRequests.length > 0 && (
          <section className="mx-3 mb-3 rounded-[10px] border border-[#e3e8ef]
            bg-[#fcfcfc] p-3" aria-label={t({ ne: 'अनुरोध', en: 'Requests' })}>
            <h3 className="mb-2 text-[11px] font-semibold tracking-[0.04em]
              text-[#4a5567]">
              {t({ ne: 'अनुरोध', en: 'REQUESTS' })}
            </h3>
            <div className="max-h-[160px] overflow-y-auto overscroll-contain
              flex flex-col gap-2">
              {tabRequests.map((message) => (
                <div key={message.id} className="flex items-center gap-3 rounded-[8px]
                  border border-[#e3e8ef] bg-white p-3">
                  <div className="min-w-0 flex-1">
                    <p className="text-[13px] leading-5 text-[#101828]">{message.body}</p>
                    <p className="text-[11px] leading-4 text-[#6a7282]">
                      {message.sender_name}
                    </p>
                  </div>
                  <div className="flex flex-none gap-2">
                    <button
                      type="button"
                      disabled={busy === message.id}
                      onClick={() => decideRequest(message, 'approve')}
                      className="rounded-[7px] bg-navy-800 px-3 py-1.5 text-[12px]
                        font-medium text-white hover:bg-navy-700 disabled:opacity-50"
                    >
                      {t({ ne: 'स्वीकार', en: 'Accept' })}
                    </button>
                    <button
                      type="button"
                      disabled={busy === message.id}
                      onClick={() => decideRequest(message, 'decline')}
                      className="rounded-[7px] border border-[#e3e8ef] bg-white
                        px-3 py-1.5 text-[12px] font-medium text-[#4a5567]
                        hover:bg-[#f3f4f6] disabled:opacity-50"
                    >
                      {t({ ne: 'अस्वीकार', en: 'Reject' })}
                    </button>
                  </div>
                </div>
              ))}
            </div>
          </section>
        )}
      </div>

      {/* What anybody in the room can put to the host, on the board it
          would appear on. There is nowhere else to write now: a message
          is either put up for everybody or it is not kept at all. */}
      {onAsk && (
        <div className="border-t border-[#e3e8ef] px-3 py-3 flex flex-col gap-1.5">
          <div className="flex items-center gap-2">
            <input
              value={writing}
              onChange={(e) => setWriting(e.target.value)}
              onKeyDown={(e) => { if (e.key === 'Enter') ask(); }}
              disabled={sending}
              aria-label={tab === 'faq'
                ? t({ ne: 'प्रश्न लेख्नुहोस्', en: 'Write your question' })
                : t({ ne: 'सुझाव लेख्नुहोस्', en: 'Write your suggestion' })}
              placeholder={t({
                ne: 'यहाँ लेख्नुहोस्', en: 'Write your message here',
              })}
              className="flex-1 min-w-0 bg-[#f7f9fc] border border-[#e3e8ef] rounded-full
                px-4 py-2 text-[14px] text-[#24262b] placeholder:text-[#8b93a3]
                focus:outline-none focus:border-navy-800 disabled:opacity-60"
            />
            <button
              onClick={ask}
              disabled={sending || !writing.trim()}
              aria-label={t({ ne: 'पठाउनुहोस्', en: 'Send' })}
              title={t({ ne: 'पठाउनुहोस्', en: 'Send' })}
              className="flex-none w-9 h-9 grid place-items-center text-navy-800
                disabled:opacity-40"
            >
              <svg
                width="20" height="20" viewBox="0 0 24 24" fill="none"
                stroke="currentColor" strokeWidth="1.8" strokeLinecap="round"
                strokeLinejoin="round" aria-hidden
              >
                <path d="M3 11.5L21 3l-8.5 18-2.5-7.5L3 11.5z" />
              </svg>
            </button>
          </div>
          <p className="text-[12px] text-[#8b93a3] leading-4">
            {t({
              ne: 'आयोजकले राखेपछि मात्र सबैले देख्छन्।',
              en: 'Everybody sees it once the host puts it up.',
            })}
          </p>
        </div>
      )}
    </div>
  );
};
